import { isServer, isDevelopment, isAnyTest, isE2E } from './executionEnvironment';
import { TupleSet, UnionOf } from './utils/typeGuardUtils';
import {initializeSetting} from './settingsCache'
import { getInstanceSettings } from './getInstanceSettings';
import { forumTypeSetting, isAF, isEAForum, isLW } from '@/lib/forumTypeUtils';
import type { FilterTag } from './filterSettings';

const getNestedProperty = function (obj: AnyBecauseTodo, desc: AnyBecauseTodo) {
  var arr = desc.split('.');
  while(arr.length && (obj = obj[arr.shift()]));
  return obj;
};

// Is any one of the arguments an object
const anyIsObject = (...args: any[]): boolean => {
  return args.some(a => typeof a === 'object' && !Array.isArray(a) && a !== null)
}

export const Settings: Record<string,any> = {};

const getSetting = <T>(settingName: string, forumType: ForumTypeString, settingDefault?: T): T => {
  let setting;
  const instanceSettings = getInstanceSettings(forumType);

  // if a default value has been registered using registerSetting, use it
  if (typeof settingDefault === 'undefined' && Settings[settingName])
    settingDefault = Settings[settingName].defaultValue;

  if (isServer) {
    // look in public, private, and root
    const rootSetting = getNestedProperty(instanceSettings, settingName);
    const privateSetting = instanceSettings.private && getNestedProperty(instanceSettings.private, settingName);
    const publicSetting = instanceSettings.public && getNestedProperty(instanceSettings.public, settingName);
    
    // if setting is an object, "collect" properties from all three places
    if (anyIsObject(rootSetting, privateSetting, publicSetting)) {
      setting = {
        ...settingDefault,
        ...rootSetting,
        ...privateSetting,
        ...publicSetting,
      };
    } else {
      if (typeof rootSetting !== 'undefined') {
        setting = rootSetting;
      } else if (typeof privateSetting !== 'undefined') {
        setting = privateSetting;
      } else if (typeof publicSetting !== 'undefined') {
        setting = publicSetting;
      } else {
        setting = settingDefault;
      }
    }

  } else {
    // look only in public
    const publicSetting = instanceSettings.public && getNestedProperty(instanceSettings.public, settingName);
    setting = typeof publicSetting !== 'undefined' ? publicSetting : settingDefault;
  }

  // Settings[settingName] = {...Settings[settingName], settingValue: setting};

  return setting;

};

/* 
  Only exported for testing purposes; do not create setting instances outside of this file!
*/
export class PublicInstanceSetting<SettingValueType> {
  constructor(
    private settingName: string, 
    private defaultValue: SettingValueType,
    private settingType: "optional" | "warning" | "required"
  ) {
    initializeSetting(settingName, "instance")
    if (isDevelopment && settingType !== "optional") {
      const lwSettingValue = getSetting(settingName, "LessWrong")
      const afSettingValue = getSetting(settingName, "AlignmentForum")
      if (typeof lwSettingValue === 'undefined' && typeof afSettingValue === 'undefined') {
        if (settingType === "warning") {
          if (!isAnyTest) {
            // eslint-disable-next-line no-console
            console.log(`No setting value provided for public instance setting for setting with name ${settingName} despite it being marked as warning`)
          }
        }
        if (settingType === "required") {
          throw Error(`No setting value provided for public instance setting for setting with name ${settingName} despite it being marked as required`)
        } 
      }
    }
  }
  get(forum: ForumTypeString | ResolverContext): SettingValueType {
    const forumType = typeof forum === 'string' ? forum : forum.forumType;
    return getSetting(this.settingName, forumType, this.defaultValue)
  }
}

/*
  Public Instance Settings
*/

export const allForumTypes = new TupleSet(["LessWrong","AlignmentForum","EAForum"] as const);
export type ForumTypeString = UnionOf<typeof allForumTypes>;
// export const forumTypeSetting = new PublicInstanceSetting<ForumTypeString>('forumType', 'LessWrong', 'warning') // What type of Forum is being run, {LessWrong, AlignmentForum, EAForum}

// eslint-disable-next-line no-barrel-files/no-barrel-files
export { forumTypeSetting, isLW, isEAForum, isAF };

export const forumTitleSetting = new PublicInstanceSetting<string>('title', 'LessWrong', 'warning') // Default title for URLs

// Your site name may be referred to as "The Alignment Forum" or simply "LessWrong". Use this setting to prevent something like "view on Alignment Forum". Leave the article uncapitalized ("the Alignment Forum") and capitalize if necessary.
export const siteNameWithArticleSetting = new PublicInstanceSetting<string>('siteNameWithArticle', "LessWrong", "warning")

// NB: Now that neither LW nor the EAForum use this setting, it's a matter of
// time before it falls out of date. Nevertheless, I expect any newly-created
// forums to use this setting.
export const hasEventsSetting = new PublicInstanceSetting<boolean>('hasEvents', true, 'optional') // Whether the current connected server has events activated

export const hasRejectedContentSectionSetting = new PublicInstanceSetting<boolean>('hasRejectedContentSection', false, 'optional');

// Sentry settings
export const sentryUrlSetting = new PublicInstanceSetting<string|null>('sentry.url', null, "warning"); // DSN URL
export const sentryEnvironmentSetting = new PublicInstanceSetting<string|null>('sentry.environment', null, "warning"); // Environment, i.e. "development"
export const sentryReleaseSetting = new PublicInstanceSetting<string|null>('sentry.release', null, "warning") // Current release, i.e. hash of lattest commit

export const siteUrlSetting = new PublicInstanceSetting<string>('siteUrl', 'http://localhost:3000/', "optional")

// FM Crossposting
export const fmCrosspostSiteNameSetting = new PublicInstanceSetting<string|null>("fmCrosspost.siteName", null, "optional");
export const fmCrosspostBaseUrlSetting = new PublicInstanceSetting<string|null>("fmCrosspost.baseUrl", null, "optional");

// For development, there's a matched set of CkEditor settings as instance
// settings, which take precedence over the database settings. This allows
// using custom CkEditor settings that don't match what's in the attached
// database.
export const ckEditorUploadUrlOverrideSetting = new PublicInstanceSetting<string | null>('ckEditorOverride.uploadUrl', null, "optional") // Image Upload URL for CKEditor
export const ckEditorWebsocketUrlOverrideSetting = new PublicInstanceSetting<string | null>('ckEditorOverride.webSocketUrl', null, "optional") // Websocket URL for CKEditor (for collaboration)

// Stripe setting

//Test vs Production Setting
export const testServerSetting = new PublicInstanceSetting<boolean>("testServer", false, "warning")

export const disableEnsureIndexSetting = new PublicInstanceSetting<boolean>("disableEnsureIndex", false, "optional");

/** Currently LW-only; forum-gated in `userCanVote` */
export const lowKarmaUserVotingCutoffDateSetting = new PublicInstanceSetting<string>("lowKarmaUserVotingCutoffDate", "11-30-2022", "optional");
/** Currently LW-only; forum-gated in `userCanVote` */
export const lowKarmaUserVotingCutoffKarmaSetting = new PublicInstanceSetting<number>("lowKarmaUserVotingCutoffKarma", 1, "optional");

/** Header-related settings */
export const taglineSetting = new PublicInstanceSetting<string>('tagline', "A community blog devoted to refining the art of rationality", "warning")
export const faviconUrlSetting = new PublicInstanceSetting<string>('faviconUrl', '/img/favicon.ico', "warning")
export const faviconWithBadgeSetting = new PublicInstanceSetting<string|null>('faviconWithBadge', null, "optional")
export const tabTitleSetting = new PublicInstanceSetting<string>('forumSettings.tabTitle', 'LessWrong', "warning")
export const tabLongTitleSetting = new PublicInstanceSetting<string | null>('forumSettings.tabLongTitle', null, "optional")

export const noIndexSetting = new PublicInstanceSetting<boolean>('noindex', false, "optional")

export const hasCuratedPostsSetting = new PublicInstanceSetting<boolean>("hasCuratedPosts", false, "optional");

export const performanceMetricLoggingEnabled = new PublicInstanceSetting<boolean>('performanceMetricLogging.enabled', false, "optional");
export const performanceMetricLoggingBatchSize = new PublicInstanceSetting<number>('performanceMetricLogging.batchSize', 100, "optional");
export const performanceMetricLoggingSqlSampleRate: number = 0.05;

export const isElasticEnabled = () => !isAnyTest && !isE2E && process.env.private_elasticsearch_disableElastic !== 'true';

export const manifoldAPIKeySetting = new PublicInstanceSetting<string | null>('manifold.reviewBotKey', null, "optional")
export const reviewUserBotSetting = new PublicInstanceSetting<string | null>('reviewBotId', null, "optional")

/** Karma threshold upon which we automatically create a market for whether this post will be a winner in the review for its year */
export const reviewMarketCreationMinimumKarmaSetting = new PublicInstanceSetting<number>('annualReviewMarket.marketCreationMinimumKarma', 100, "optional");
/** Minimum market odds required to highlight this post (e.g. make its karma gold) as a potential review winner */
export const highlightReviewWinnerThresholdSetting = new PublicInstanceSetting<number>('annualReviewMarket.highlightReviewWinnerThreshold', 0.25, "optional");

export const myMidjourneyAPIKeySetting = new PublicInstanceSetting<string | null>('myMidjourney.apiKey', null, "optional");
export const maxAllowedApiSkip = new PublicInstanceSetting<number | null>("maxAllowedApiSkip", 2000, "optional")

export const recombeeDatabaseIdSetting = new PublicInstanceSetting<string | null>('recombee.databaseId', null, "optional");
export const recombeePublicApiTokenSetting = new PublicInstanceSetting<string | null>('recombee.publicApiToken', null, "optional");
export const recombeePrivateApiTokenSetting = new PublicInstanceSetting<string | null>('recombee.privateApiToken', null, "optional");

export type PostFeedDetails = {
  name: string,
  label: string,
  description?: string,
  disabled?: boolean,
  adminOnly?: boolean,
  showLabsIcon?: boolean,
  isInfiniteScroll?: boolean,
  slug?: string,
  showToLoggedOut?: boolean,
}

export const homepagePostFeedsSetting = new PublicInstanceSetting<PostFeedDetails[]>('homepagePosts.feeds', [
    {
      'name': 'forum-classic',
      'label': 'Latest',
      'description': 'The classic LessWrong frontpage algorithm that combines karma with time discounting, plus any tag-based weighting if applied.',
    },
    {
      'name': 'forum-bookmarks',
      'label': 'Bookmarks',
      'description': 'A list of posts you saved because you wanted to have them findable later.',
    },
    {
      'name': 'forum-continue-reading',
      'label': 'Resume Reading',
      'description': 'Further posts in post sequences that you started reading.',
    },
  ]
  , 'optional')

export const recombeeCacheTtlMsSetting = new PublicInstanceSetting<number>('recombee.cacheTtlMs', 1000 * 60 * 60 * 24 * 30, "optional");

export const aboutPostIdSetting = new PublicInstanceSetting<string>('aboutPostId', 'bJ2haLkcGeLtTWaD5', "warning") // Post ID for the /about route

export const falApiKey = new PublicInstanceSetting<string>('falAI.apiKey', "", "optional")

export const jargonBotClaudeKey = new PublicInstanceSetting<string>('anthropic.jargonBotClaudeKey', "", "optional")

export const hyperbolicApiKey = new PublicInstanceSetting<string>('hyperbolic.apiKey', "", "optional")

export const twitterBotEnabledSetting = new PublicInstanceSetting<boolean>("twitterBot.enabled", false, "optional");
export const twitterBotKarmaThresholdSetting = new PublicInstanceSetting<number>("twitterBot.karmaThreshold", 40, "optional");

export const forumHeaderTitleSetting = new PublicInstanceSetting<string>('forumSettings.headerTitle', "LESSWRONG", "warning");
export const forumShortTitleSetting = new PublicInstanceSetting<string>('forumSettings.shortForumTitle', "LW", "warning");

export const allowTypeIIIPlayerSetting = new PublicInstanceSetting<boolean>('allowTypeIIIPlayer', false, "optional");

export const faqPostIdSetting = new PublicInstanceSetting<string>('faqPostId', '2rWKkWuPrgTMpLRbp', "warning"); // Post ID for the /faq route
export const contactPostIdSetting = new PublicInstanceSetting<string>('contactPostId', "ehcYkvyz7dh9L7Wt8", "warning");
export const introPostIdSetting = new PublicInstanceSetting<string | null>('introPostId', null, "optional");

export const instanceDebuggers: string[] = [];

// Since different environments are connected to the same DB, this setting cannot be moved to the database
export const environmentDescriptionSetting = new PublicInstanceSetting<string>("analytics.environment", "misconfigured", "warning");

// For development, there's a matched set of CkEditor settings as instance
// settings, which take precedence over the database settings. This allows
// using custom CkEditor settings that don't match what's in the attached
// database.

export const searchOriginDate = "2003-01-01T01:00:00Z";

// Database ID string that this config file should match with
export const expectedDatabaseIdSetting = new PublicInstanceSetting<string | null>('expectedDatabaseId', null, "warning");

export const apiKeySetting = new PublicInstanceSetting<string | null>("twitterBot.apiKey", null, "optional");
export const apiKeySecretSetting = new PublicInstanceSetting<string | null>("twitterBot.apiKeySecret", null, "optional");
export const accessTokenSetting = new PublicInstanceSetting<string | null>("twitterBot.accessToken", null, "optional");
export const accessTokenSecretSetting = new PublicInstanceSetting<string | null>("twitterBot.accessTokenSecret", null, "optional");
/*
  Public Database Settings
*/

export const googleTagManagerId: string = "GTM-TRC765W"; // Google Tag Manager ID
export const reCaptchaSiteKey: string = "6LfFgqEUAAAAAHKdMgzGO-1BRBhHw1x6_8Ly1cXc"; // ReCaptcha API Key
// Despite the name, this setting is also used to set the index prefix for Elasticsearch for legacy reasons
export const algoliaIndexPrefix: string = "test_";

export const ckEditorUploadUrlSetting = new PublicInstanceSetting<string | null>('ckEditor.uploadUrl', null, "optional"); // Image Upload URL for CKEditor
export const ckEditorWebsocketUrlSetting = new PublicInstanceSetting<string | null>('ckEditor.webSocketUrl', null, "optional"); // Websocket URL for CKEditor (for collaboration)

export const hideUnreviewedAuthorCommentsSettings = new PublicInstanceSetting<string | null>('hideUnreviewedAuthorComments', null, "optional"); // Hide comments by unreviewed authors after date provided (prevents spam / flaming / makes moderation easier, but delays new user engagement)
export const cloudinaryCloudName: string = "lesswrong-2-0"; // Cloud name for cloudinary hosting

export const nofollowKarmaThreshold = new PublicInstanceSetting<number>('nofollowKarmaThreshold', 10, "optional"); // Users with less than this much karma have their links marked as nofollow

export const localeSetting = new PublicInstanceSetting<string>('locale', 'en-US', "optional");
export const legacyRouteAcronymSetting = new PublicInstanceSetting<string>('legacyRouteAcronym', 'lw', "optional"); // Because the EA Forum was identical except for the change from /lw/ to /ea/
// frontpageFilterSettings default tag filter
//
// At the risk of premature future-proofing, this setting, which is initially
// here to allow the EA Forum to nudge down the visibility of posts with the
// Community tag, can be trivially applied to personalBlog, frontpage, and
// curated, if those ever get refactored into tags.
export const defaultVisibilityTags = new PublicInstanceSetting<Array<FilterTag>>('defaultVisibilityTags', [], "optional");

// Public elicit settings
export const elicitSourceURL = new PublicInstanceSetting('elicitSourceURL', 'https://LessWrong.com', "optional");
export const elicitSourceId = new PublicInstanceSetting('elicitSourceId', 'XCjOpumu-', "optional");

export const mapboxAPIKeySetting = new PublicInstanceSetting<string | null>('mapbox.apiKey', null, "optional"); // API Key for the mapbox map and tile requests

export const isProductionDBSetting = new PublicInstanceSetting<boolean>('isProductionDB', false, "optional");

export const showReviewOnFrontPageIfActive = new PublicInstanceSetting<boolean>('annualReview.showReviewOnFrontPageIfActive', true, "optional");

// these are deprecated, but preserved for now in case we want to revert
// export const annualReviewStart = new PublicInstanceSetting('annualReview.start', "2021-11-30", "optional")
// // The following dates cut off their phase at the end of the day
// export const annualReviewNominationPhaseEnd = new PublicInstanceSetting('annualReview.nominationPhaseEnd', "2021-12-14", "optional")
// export const annualReviewReviewPhaseEnd = new PublicInstanceSetting('annualReview.reviewPhaseEnd', "2022-01-15", "optional")
// export const annualReviewVotingPhaseEnd = new PublicInstanceSetting('annualReview.votingPhaseEnd', "2022-02-01", "optional")
// export const annualReviewEnd = new PublicInstanceSetting('annualReview.end', "2022-02-06", "optional")

export const annualReviewAnnouncementPostPathSetting = new PublicInstanceSetting<string | null>('annualReview.announcementPostPath', null, "optional");

export const annualReviewVotingResultsPostPath = new PublicInstanceSetting<string>('annualReview.votingResultsPostPath', "", "optional");

export const moderationEmail = new PublicInstanceSetting<string>('moderationEmail', "ERROR: NO MODERATION EMAIL SET", "optional");

type AccountInfo = {
  _id: string;
  username: string;
  email: string;
};
export const adminAccountSetting = new PublicInstanceSetting<AccountInfo | null>('adminAccount', null, "optional");

export const seasonalOpenThreadAuthorSlugSetting = new PublicInstanceSetting<string>('seasonalOpenThread.authorSlug', 'habryka4', "optional");

export const crosspostKarmaThreshold = new PublicInstanceSetting<number | null>('crosspostKarmaThreshold', 100, "optional");

export const hasCookieConsent: boolean = false;

// Null means requests are disabled
export const requestFeedbackKarmaLevelSetting = new PublicInstanceSetting<number | null>('post.requestFeedbackKarmaLevel', 100, "optional");

export const showSubscribeReminderInFeed = new PublicInstanceSetting<boolean>('feed.showSubscribeReminder', true, "optional");

export const hasGoogleDocImportSetting = new PublicInstanceSetting<boolean>('googleDocImport.enabled', false, "optional");

export const recombeeEnabledSetting = new PublicInstanceSetting<boolean>('recombee.enabled', false, "optional");
export const recommendationsTabManuallyStickiedPostIdsSetting = new PublicInstanceSetting<string[]>('recommendationsTab.manuallyStickiedPostIds', [], "optional");

export const blackBarTitle = new PublicInstanceSetting<string | null>('blackBarTitle', null, "optional");

/** Whether to show permalinked (?commentId=...) comments at the top of the page, vs scrolling to show them in context */
export const commentPermalinkStyleSetting = new PublicInstanceSetting<'top' | 'in-context'>('commentPermalinkStyle', 'top', "optional");

export const userIdsWithAccessToLlmChat = new PublicInstanceSetting<string[]>('llmChat.userIds', [], "optional");

export const textReplacementsSetting = new PublicInstanceSetting<Record<string, string>>('textReplacements', {}, "optional");

export const lightconeFundraiserPaymentLinkId: string = "plink_1QPdGLBlb9vL5IMTvkJ3LZ6v";
export const lightconeFundraiserThermometerBgUrl = new PublicInstanceSetting<string>('lightconeFundraiser.thermometerBgUrl', '', "optional");
export const lightconeFundraiserThermometerGoalAmount = new PublicInstanceSetting<number>('lightconeFundraiser.thermometerGoalAmount', 0, "optional");
export const lightconeFundraiserThermometerGoal2Amount = new PublicInstanceSetting<number>('lightconeFundraiser.thermometerGoal2Amount', 2000000, "optional");
export const lightconeFundraiserThermometerGoal3Amount = new PublicInstanceSetting<number>('lightconeFundraiser.thermometerGoal3Amount', 3000000, "optional");
export const lightconeFundraiserPostId = new PublicInstanceSetting<string>('lightconeFundraiser.postId', '', "optional");
export const lightconeFundraiserActive = new PublicInstanceSetting<boolean>('lightconeFundraiser.active', false, "optional");

export const postsListViewTypeSetting = new PublicInstanceSetting<string>('posts.viewType', 'list', "optional");
export const quickTakesMaxAgeDaysSetting = new PublicInstanceSetting<number>('feed.quickTakesMaxAgeDays', 5, "optional");

export const mapsAPIKey: string = "AIzaSyA3C48rl26gynG3qIuNuS-3Bh_Zz9jFXkY";

export const siteImageSetting = new PublicInstanceSetting<string>('siteImage', 'https://res.cloudinary.com/lesswrong-2-0/image/upload/v1654295382/new_mississippi_river_fjdmww.jpg', "optional"); // An image used to represent the site on social media

export const ultraFeedEnabledSetting = new PublicInstanceSetting<boolean>('ultraFeedEnabled', false, "optional");

export const petrovBeforeTime = new PublicInstanceSetting<number>('petrov.beforeTime', 0, "optional");
export const petrovAfterTime = new PublicInstanceSetting<number>('petrov.afterTime', 0, "optional");

export const openThreadTagIdSetting = new PublicInstanceSetting<string>('openThreadTagId', 'eTLv8KzwBGcDip9Wi', "optional");
export const startHerePostIdSetting = new PublicInstanceSetting<string | null>('startHerePostId', null, "optional");
export const amaTagIdSetting = new PublicInstanceSetting<string | null>('amaTagId', null, "optional");

export const defaultSequenceBannerIdSetting = new PublicInstanceSetting<string | null>("defaultSequenceBannerId", null, "optional");

export const graphqlBatchMaxSetting = new PublicInstanceSetting('batchHttpLink.batchMax', 50, "optional");

export const firstCommentAcknowledgeMessageCommentIdSetting = new PublicInstanceSetting<string>('firstCommentAcknowledgeMessageCommentId', '', "optional");

export const ipApiKey: string|null = null;

export const intercomAppIdSetting = new PublicInstanceSetting<string>('intercomAppId', 'wtb8z7sj', "optional");

export const maintenanceTime = new PublicInstanceSetting<string | null>("maintenanceBannerTime", null, "optional");
export const explanationText = new PublicInstanceSetting<string>("maintenanceBannerExplanationText", "", "optional");
export const showSmallpoxSetting = new PublicInstanceSetting<boolean>('showSmallpox', false, "optional");
export const showEventBannerSetting = new PublicInstanceSetting<boolean>('showEventBanner', false, "optional");
export const showMaintenanceBannerSetting = new PublicInstanceSetting<boolean>('showMaintenanceBanner', false, "optional");
export const eventBannerMobileImageSetting = new PublicInstanceSetting<string | null>('eventBannerMobileImage', null, "optional");
export const eventBannerDesktopImageSetting = new PublicInstanceSetting<string | null>('eventBannerDesktopImage', null, "optional");
export const eventBannerLinkSetting = new PublicInstanceSetting<string | null>('eventBannerLink', null, "optional");

export const placeholderSetting = new PublicInstanceSetting<string>("linkpostUrlPlaceholder", "http://example.com/blog/2017/reality-has-a-surprising-amount-of-detail", "optional");

export const disableCookiePreferenceAutoUpdateSetting = new PublicInstanceSetting<boolean>('disableCookiePreferenceAutoUpdate', false, "optional");

export const cloudinaryUploadPresetGridImageSetting = new PublicInstanceSetting<string>("cloudinary.uploadPresetGridImage", "tz0mgw2s", "optional");
export const cloudinaryUploadPresetBannerSetting = new PublicInstanceSetting<string>("cloudinary.uploadPresetBanner", "navcjwf7", "optional");
export const cloudinaryUploadPresetProfileSetting = new PublicInstanceSetting<string | null>("cloudinary.uploadPresetProfile", null, "optional");
export const cloudinaryUploadPresetSocialPreviewSetting = new PublicInstanceSetting<string | null>("cloudinary.uploadPresetSocialPreview", null, "optional");
export const cloudinaryUploadPresetEventImageSetting = new PublicInstanceSetting<string | null>("cloudinary.uploadPresetEventImage", null, "optional");
export const cloudinaryUploadPresetSpotlightSetting = new PublicInstanceSetting<string | null>("cloudinary.uploadPresetSpotlight", "yjgxmsio", "optional");
export const cloudinaryUploadPresetDigestSetting = new PublicInstanceSetting<string | null>("cloudinary.uploadPresetDigest", null, "optional");

export const bookDisplaySetting = new PublicInstanceSetting<boolean>('bookDisplaySetting', false, "optional");

export const petrovPostIdSetting = new PublicInstanceSetting<string>('petrov.petrovPostId', '', "optional");
export const petrovGamePostIdSetting = new PublicInstanceSetting<string>('petrov.petrovGamePostId', '', "optional");

export const defaultAFModeratorPMsTagSlug = new PublicInstanceSetting<string>('defaultAFModeratorPMsTagSlug', "af-default-moderator-responses", "optional");
export const commentModerationWarningCommentIdSetting = new PublicInstanceSetting<string>('commentModerationWarningCommentId', '', "optional");
export const postModerationWarningCommentIdSetting = new PublicInstanceSetting<string>('postModerationWarningCommentId', '', "optional");

export const showAnalyticsDebug: "never" | "dev" | "always" = "dev";

export const type3DateCutoffSetting = new PublicInstanceSetting<string>('type3.cutoffDate', '2023-05-01', "optional");
export const type3ExplicitlyAllowedPostIdsSetting = new PublicInstanceSetting<string[]>('type3.explicitlyAllowedPostIds', [], "optional");
/** type3KarmaCutoffSetting is here to allow including high karma posts from before type3DateCutoffSetting */
export const type3KarmaCutoffSetting = new PublicInstanceSetting<number>('type3.karmaCutoff', Infinity, "optional");
export const newUserIconKarmaThresholdSetting = new PublicInstanceSetting<number | null>('newUserIconKarmaThreshold', null, "optional");
export const cloudinaryUploadPresetEditorName = new PublicInstanceSetting<string | null>('cloudinary.uploadPresetEditor', null, "optional");

// LW (and legacy) time decay algorithm settings
export const timeDecayFactor = 1.15;
export const frontpageScoreBonus: number = 0;
export const curatedScoreBonus: number = 10;

// EA Frontpage time decay algorithm settings
export const defaultStartingAgeHours: number = 6;
export const defaultDecayFactorSlowest: number = 0.5;
export const defaultDecayFactorFastest: number = 1.08;
export const defaultActivityWeight: number = 1.4;
export const defaultActivityHalfLife: number = 60;
export const frontpageDaysAgoCutoffSetting = new PublicInstanceSetting<number>('frontpageAlgorithm.daysAgoCutoff', 90, "optional");
export const databaseDebuggers: string[] = [];

// 'Maximum documents per request'
export const maxDocumentsPerRequestSetting = new PublicInstanceSetting<number>('maxDocumentsPerRequest', 10000, "optional");

export const addNewReactKarmaThreshold = 10;
export const addNameToExistingReactKarmaThreshold = 5;
export const downvoteExistingReactKarmaThreshold = 20;

export const karmaRewarderId100: string = "iqWr6C3oEB4yWpzn5";
export const karmaRewarderId1000: string = "mBBmKWkmw8bgJmGiG";

export const logoUrlSetting = new PublicInstanceSetting<string | null>('logoUrl', null, "optional");
